import * as Sentry from '@sentry/react';

export function initializeSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const enabled = import.meta.env.VITE_SENTRY_ENABLED === 'true';
  const environment = import.meta.env.VITE_APP_ENVIRONMENT || 'unknown';
  const domain = import.meta.env.VITE_APP_DOMAIN || 'unknown';
  const verbose = import.meta.env.VITE_APP_VERBOSE === 'true';

  if (!enabled || !dsn) {
    if (verbose) {
      console.info('🔒 Sentry: Disabled for this environment');
    }
    return;
  }

  const version = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0';
  const buildDate = new Date().toISOString();

  try {
    Sentry.init({
      dsn: dsn,
      environment: environment,
      release: `widget-chatbot-utn@${version}`,

      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }),
      ],

      tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '1.0'),

      replaysSessionSampleRate: parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE || '0.1'),
      replaysOnErrorSampleRate: parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE || '1.0'),

      sendDefaultPii: true,

      beforeSend(event, hint) {
        if (verbose) {
          console.log('📤 Sentry: Capturing event', {
            type: event.type,
            message: event.message,
            level: event.level
          });
        }
        return event;
      },

      ignoreErrors: [
        'ResizeObserver loop limit exceeded',
        'Non-Error promise rejection captured',
      ],
    });

    Sentry.setTags({
      version: version,
      environment: environment,
      build_date: buildDate,
      domain: domain,
    });

    Sentry.setContext('build', {
      version: version,
      environment: environment,
      buildDate: buildDate,
      domain: domain,
    });

    if (verbose) {
      console.info('✅ Sentry initialized successfully');
      console.info('📊 Sentry Configuration:', {
        release: `widget-chatbot-utn@${version}`,
        environment: environment,
        domain: domain,
        tracesSampleRate: import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '1.0',
      });
    }

  } catch (error) {
    console.error('❌ Failed to initialize Sentry:', error);
  }
}

export function setSentryUser(userId, userData = {}) {
  if (import.meta.env.VITE_SENTRY_ENABLED !== 'true') {
    return;
  }

  try {
    Sentry.setUser({
      id: userId,
      ...userData
    });
  } catch (error) {
    console.error('Failed to set Sentry user:', error);
  }
}

export function clearSentryUser() {
  if (import.meta.env.VITE_SENTRY_ENABLED !== 'true') {
    return;
  }

  try {
    Sentry.setUser(null);
  } catch (error) {
    console.error('Failed to clear Sentry user:', error);
  }
}

export function captureSentryException(error, context = {}) {
  if (import.meta.env.VITE_SENTRY_ENABLED !== 'true') {
    console.error('Error (Sentry disabled):', error, context);
    return;
  }

  try {
    Sentry.captureException(error, {
      contexts: {
        custom: context
      }
    });
  } catch (e) {
    console.error('Failed to capture exception in Sentry:', e);
  }
}

export function captureSentryMessage(message, level = 'info', context = {}) {
  if (import.meta.env.VITE_SENTRY_ENABLED !== 'true') {
    console.log(`Message (Sentry disabled) [${level}]:`, message, context);
    return;
  }

  try {
    Sentry.captureMessage(message, level);
  } catch (error) {
    console.error('Failed to capture message in Sentry:', error);
  }
}

export function addSentryBreadcrumb(message, category = 'custom', level = 'info', data = {}) {
  if (import.meta.env.VITE_SENTRY_ENABLED !== 'true') {
    return;
  }

  try {
    Sentry.addBreadcrumb({
      message,
      category,
      level,
      data
    });
  } catch (error) {
    console.error('Failed to add Sentry breadcrumb:', error);
  }
}

export { Sentry };

